import { useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { Box, Title } from '@factorialco/deprecated-design-system'
import { TrashRootPageDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import useGetDocumentCounters from 'modules/documents/hooks/documents/useGetDocumentCounters'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export enum TemplateTabId {
  'employee',
  'company_public',
  'company_internal',
}

interface Tab {
  name: string
  href: string
}

const TrashRootPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { loading, data, error } = useQuery(TrashRootPageDocument, {})

  const useDocuments = data?.documents?._policies?.companyUseCompanyDocuments.can
  const canSeeEmployeeTab =
    useDocuments ||
    data?.documents?._policies?.employeeEditDocumentsTrashBin.can ||
    data?.documents?._policies?.employeeManageAnyEmployeeFolder.can ||
    data?.documents?._policies?.employeeManageDefaultEmployeeFolder.can ||
    data?.documents?._policies?.employeeEditAnyEmployeeFolder.can ||
    data?.documents?._policies?.employeeDeleteAnyEmployeeFolder.can
  const canSeePublicTab = useDocuments || data?.documents?._policies?.companyEditPublicDocuments.can
  const canSeeInternalTab =
    useDocuments || data?.documents?._policies?.companyEditInternalDocuments.can
  const [tabs, setTabs] = useState<Tab[]>([])

  const { employeeCounter, companyInternalCounter, companyPublicCounter } = useGetDocumentCounters({
    byTrashBin: true,
  })

  const buildTabName = (title: string, counter?: number | null) =>
    counter ? `${title} (${counter})` : title

  const authorized = !loading && (canSeeEmployeeTab || canSeePublicTab || canSeeInternalTab)

  useUnsafeEffect(() => {
    const initialTabs = []

    if (canSeeEmployeeTab) {
      initialTabs.push({
        name: buildTabName(i18n.t('documents.trash.sections.employee'), employeeCounter),
        href: navigation.files.trash.employee.fullPath(),
      })
    }

    if (canSeePublicTab) {
      initialTabs.push({
        name: buildTabName(i18n.t('documents.trash.sections.company_public'), companyPublicCounter),
        href: navigation.files.trash.company_public.fullPath(),
      })
    }

    if (canSeeInternalTab) {
      initialTabs.push({
        name: buildTabName(
          i18n.t('documents.trash.sections.company_internal'),
          companyInternalCounter
        ),
        href: navigation.files.trash.company_internal.fullPath(),
      })
    }

    setTabs(initialTabs)
  }, [
    canSeeEmployeeTab,
    canSeePublicTab,
    canSeeInternalTab,
    navigate,
    navigation.files.trash.employee,
    navigation.files.trash.company_public,
    navigation.files.trash.company_internal,
    employeeCounter,
    companyInternalCounter,
    companyPublicCounter,
  ])

  useUnsafeEffect(() => {
    const validPaths = tabs.map((tab) => tab.href)

    if (validPaths.some((validPath) => pathname.startsWith(validPath))) return

    const tab = tabs[0]

    if (tab) navigate(tab.href)
  }, [navigate, pathname, tabs])

  return (
    <ResponsivePage.Body loading={loading} error={error} showHeader={false} authorized={authorized}>
      <Title
        title=""
        description={i18n.t('documents.trash.description')}
        link={{
          href: helpCenterUrl('documents_in_bulk'),
          label: i18n.t('common.learn_more'),
        }}
        variant="500"
      />
      <Box>
        <Outlet />
      </Box>
    </ResponsivePage.Body>
  )
}

export default TrashRootPage
